import React from 'react'
import { Heading, Text } from '@primer/react-brand'

import PlanSummaryCard from '../components/PlanSummaryCard'
import StripeCardForm from '../components/StripeCardForm'
import { PLAN_PRICES } from '../../components/subscriptionConstants'
import { API_BASE_URL } from '../../config/apiConfig'

// Screen 5b — Payment details. Reached after the user selects a plan on the
// pricing page. Collects a real card through the shared StripeCardForm and SAVES
// it (setup mode — no charge yet); the card is charged later, either on-session
// here if the org is already approved, or off-session when an admin approves.
//
// Renders content-only into the header-only app chrome (civic_lens_subscribe
// layout); no page Header/Footer of its own. Design: PaymentScreen in
// Prototype C_A_primer_v2.html (.apply-* classes).
//
// Props: { plan, returnTo, stripePublishableKey, orgApproved }
//
// orgApproved decides the copy. An org that was approved before subscribing is
// charged the moment its card is saved (UC-3 ext 7a), so the page must say so;
// the "you won't be charged until approved" wording is only true for an org
// that still has to apply.

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

const PaymentDetailsPage = ({ plan, returnTo, stripePublishableKey, orgApproved = false }) => {
  const normalizedPlan = (plan || '').toLowerCase()

  // Guard: a missing/invalid plan means the user didn't come through the pricing
  // page — send them back to choose one.
  if (!PLAN_PRICES[normalizedPlan]) {
    if (typeof window !== 'undefined') window.location.href = '/membership'
    return null
  }

  const applyHref = returnTo
    ? `/subscribe/apply?return_to=${encodeURIComponent(returnTo)}`
    : '/subscribe/apply'

  // Persist the saved card on our side; the backend may also charge on-session
  // (already-approved org) and will surface a decline. Returns an error string for
  // the form to display, or navigates away on success.
  const handleConfirmed = async (setupIntentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v2/organizations/save_payment_method`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({ plan: normalizedPlan, setup_intent_id: setupIntentId }),
      })
      const data = await response.json().catch(() => ({}))

      if (response.ok && data.success) {
        // subscribed === true means the on-session charge went through, so the org
        // is already active — go straight to the app. Otherwise continue to Apply.
        window.location.href = data.subscribed ? '/monitoring' : applyHref
        return null
      }

      // Errors may be plain strings (our 422s) or JSON:API error objects (a 500) —
      // extract a displayable string either way so we never render an object.
      const first = Array.isArray(data.errors) ? data.errors[0] : null
      return (typeof first === 'string' && first) || first?.detail ||
        'Something went wrong saving your card. Please try again.'
    } catch (err) {
      return 'Something went wrong saving your card. Please try again.'
    }
  }

  return (
    <div style={{ maxWidth: 960, width: '100%', margin: '0 auto', padding: '52px 40px 96px', fontFamily: 'var(--brand-body-fontFamily)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 56, alignItems: 'flex-start' }}>
          {/* Left — plan summary card */}
          <PlanSummaryCard plan={normalizedPlan} />

          {/* Right — payment form */}
          <div>
            <Text as="p" style={{ marginBottom: 14, fontSize: 13 }}>
              <span
                style={{ color: 'var(--base-color-scale-blue-5)', cursor: 'pointer' }}
                onClick={() => { window.location.href = '/membership' }}
              >
                ← Back to plans
              </span>
            </Text>

            <Heading as="h1" size="5">Payment details</Heading>
            <Text as="p" size="300" variant="muted" style={{ marginTop: 6, marginBottom: 28, lineHeight: 1.65 }}>
              {orgApproved
                ? <>Enter your card details below. Your organization is already approved, so your card will be charged {PLAN_PRICES[normalizedPlan]} now and your subscription starts today.</>
                : <>Enter your card details below. You won&rsquo;t be charged until your application is reviewed and approved.</>}
            </Text>

            <StripeCardForm
              stripePublishableKey={stripePublishableKey}
              submitLabel={orgApproved ? 'Subscribe & pay now →' : 'Save card & continue →'}
              secondaryAction={{ label: 'Change plan', onClick: () => { window.location.href = '/membership' } }}
              onConfirmed={handleConfirmed}
            />
          </div>
        </div>
      </div>
  )
}

export default PaymentDetailsPage
